import React, { useEffect, useState, useCallback, useRef } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import { Checkbox } from '../../../components/ui/Checkbox';
import { useNotification } from '../../../components/ui/NotificationProvider';
import mercadoLibreService from '../../../services/mercadoLibreService';
import PricingPreview, { getVerifiedPricingRows , getAvailablePricingRows } from './PricingPreview';

const BulkEditPanel = ({ 
  selectedCount = 0, 
  onBulkUpdate, 
  onClearSelection,
  isVisible = false,
  sourceFileName = '',
  usdToCopRateStatus = 'loading',
  formattedUsdToCopRate = null,
  formattedUsdToCopFetchedAt = null,
  batchCategoryId = '',
  batchCategoryPath = '',
  selectedProducts = [],
  attributeExportBatches = []
}) => {
  const [activeTab, setActiveTab] = useState('category');
  const [bulkChanges, setBulkChanges] = useState({
    category: '',
    categoryPath: '',
    priceAdjustment: { type: 'percentage', value: 0 },
    // descriptionTemplate removido: la descripción viene del archivo
    condition: '',
    shipping: { free: false, mode: 'me2' },
    manufacturingTimeDays: ''
  });
  const [manufacturingTimeError, setManufacturingTimeError] = useState('');
  const [storeName, setStoreName] = useState('');
  const [storeSavedAt, setStoreSavedAt] = useState(null);
  const [applyProgress, setApplyProgress] = useState(null);
  const applyingRef = useRef(false);

  // Vista previa del motor real de precios.
  // IMPORTANTE: solo calcula una muestra para no procesar miles de productos
  // simplemente por abrir Edición Masiva.
  const PRICING_PREVIEW_LIMIT = 20;
  const [pricingPreviewRows, setPricingPreviewRows] = useState([]);
  const [pricingPreviewLoading, setPricingPreviewLoading] = useState(false);
  const [pricingPreviewProgress, setPricingPreviewProgress] = useState({
    done: 0,
    total: 0
  });
  const [pricingPreviewRefreshKey, setPricingPreviewRefreshKey] = useState(0);

  const { showError } = useNotification();
  const [rootCategoryOptions, setRootCategoryOptions] = useState([]);
  const [categoryLevels, setCategoryLevels] = useState([]);
  const [categoryPathSelections, setCategoryPathSelections] = useState([]);
  const [categoryStatusMessage, setCategoryStatusMessage] = useState('Selecciona una categoría y navega hasta una subcategoría hoja.');
  const [loadingCategories, setLoadingCategories] = useState(false);
  const [loadingChildrenLevel, setLoadingChildrenLevel] = useState(false);
  const [categoryLoadError, setCategoryLoadError] = useState(null);
  const [legacyCategoryOptions, setLegacyCategoryOptions] = useState([]);
  const [manualCategoryId, setManualCategoryId] = useState('');
  const [manualCategoryError, setManualCategoryError] = useState('');
  const [manualCategoryLoading, setManualCategoryLoading] = useState(false);
  const [attributePreviews, setAttributePreviews] = useState([]);
  const [attributePreviewLoading, setAttributePreviewLoading] = useState(false);
  const [attributePreviewError, setAttributePreviewError] = useState('');
  const [selectedAttributePreviewKey, setSelectedAttributePreviewKey] = useState('');
  const [attributePreviewMetrics, setAttributePreviewMetrics] = useState(null);

  // MCO_EXPORTAR_SOLO_FALTANTES_V1
  // Lectura de todos los lotes; no modifica ni publica productos.
  const [exportandoFaltantes, setExportandoFaltantes] = useState(false);
  const [estadoExportacion, setEstadoExportacion] = useState('');

  const descargarFaltantesMCO = async () => {
    if (exportandoFaltantes) return;

    const registros = [];

    (attributeExportBatches || []).forEach(batch => {
      if (!Array.isArray(batch?.products)) return;

      const archivo = String(batch.fileName || 'archivo.json');
      const categoriaArchivo =
        archivo.match(/\bMCO\d+\b/i)?.[0]?.toUpperCase() || '';

      batch.products.forEach((product, index) => {
        if (!product || typeof product !== 'object') return;

        registros.push({
          archivo,
          posicion: index + 1,
          product,
          categoria:
            batch?.categoryOverride ||
            batch?.detectedCategoryId ||
            batch?.detectedCategory ||
            categoriaArchivo ||
            product?.category_id ||
            ''
        });
      });
    });

    if (!registros.length) {
      setEstadoExportacion('No hay productos cargados.');
      return;
    }

    const attributeId = item =>
      String(
        typeof item === 'string'
          ? item
          : item?.id || item?.attribute_id || item?.name || ''
      ).trim();

    setExportandoFaltantes(true);
    setEstadoExportacion(`Iniciando revisión de ${registros.length} productos...`);

    let verificados = 0;
    let noVerificados = 0;
    let productosConFaltantes = 0;
    let totalFaltantes = 0;

    const detalles = [];

    try {
      // Grupos pequeños para reutilizar el motor existente y
      // su separación automática de solicitudes grandes.
      for (let offset = 0; offset < registros.length; offset += 10) {
        const grupo = registros.slice(offset, offset + 10);

        let respuesta = null;

        try {
          const copias = grupo.map(item => {
            const copia = JSON.parse(JSON.stringify(item.product));

            // Solo en la copia de consulta; nunca en el JSON cargado.
            if (item.categoria) {
              copia.category_id = item.categoria;
            }

            return copia;
          });

          respuesta = await mercadoLibreService.previewPreparedAttributes(copias);
        } catch (error) {
          console.warn(
            '[Informe MCO] Grupo no verificado:',
            error?.message || String(error)
          );
        }

        const previews = Array.isArray(respuesta?.previews)
          ? respuesta.previews
          : [];

        grupo.forEach((item, index) => {
          const preview = previews[index];
          const prep = preview?.attributePreparation;

          if (
            !prep ||
            preview?.preparationError ||
            !Array.isArray(prep.missing)
          ) {
            noVerificados++;
            return;
          }

          verificados++;

          const pendientes = [...prep.missing];

          // Incluir MODEL solo cuando es obligatorio
          // y el diagnóstico lo indica como perdido o faltante.
          const modeloObligatorio = (prep.required || []).some(
            attr => attributeId(attr).toUpperCase() === 'MODEL'
          );

          const estadoModelo = prep.model?.status;

          if (
            modeloObligatorio &&
            ['missing', 'lost_during_processing'].includes(estadoModelo) &&
            !pendientes.some(
              attr => attributeId(attr).toUpperCase() === 'MODEL'
            )
          ) {
            pendientes.push({
              id: 'MODEL',
              reason: prep.model?.reason || estadoModelo
            });
          }

          // FILTRO PRINCIPAL:
          // excluir absolutamente todos los productos sin faltantes.
          if (!pendientes.length) return;

          productosConFaltantes++;
          totalFaltantes += pendientes.length;

          const product = item.product;

          const asin =
            product?.asin ||
            product?.ASIN ||
            product?.amazon_asin ||
            product?.id ||
            'SIN_ASIN';

          const titulo =
            product?.title ||
            product?.titulo ||
            product?.name ||
            'SIN_TITULO';

          const obligatorios = Array.isArray(prep.required)
            ? prep.required
            : [];


          // MCO_REPORTE_MINIMO_V2
          // Solo MCO, ASIN, título y atributos faltantes.
          const faltantesTexto = pendientes.map(missing => {
            const id = attributeId(missing);

            const definicion = obligatorios.find(
              attr => attributeId(attr).toUpperCase() ===
                id.toUpperCase()
            );

            const nombre =
              definicion?.name ||
              missing?.name ||
              id;

            return nombre && id && nombre !== id
              ? `${nombre} (${id})`
              : (id || nombre || 'ATRIBUTO_SIN_ID');
          });

          detalles.push([
            `MCO: ${item.categoria || preview?.category_id || product?.category_id || 'SIN_CATEGORIA'}`,
            `ASIN: ${asin}`,
            `TÍTULO: ${titulo}`,
            `ATRIBUTOS FALTANTES: ${faltantesTexto.join(' | ')}`
          ].join('\n'));

        });

        setEstadoExportacion(
          `Revisando ${Math.min(offset + grupo.length, registros.length)}` +
          ` de ${registros.length} productos...`
        );
      }

      if (verificados === 0) {
        setEstadoExportacion(
          'No se pudo verificar ningún producto. No se generó un informe engañoso.'
        );
        return;
      }

      const fecha = new Date();

      const contenido = detalles.join('\n\n');

      const blob = new Blob(
        ['\uFEFF', contenido],
        { type: 'text/plain;charset=utf-8' }
      );

      const url = URL.createObjectURL(blob);
      const enlace = document.createElement('a');

      enlace.href = url;
      enlace.download =
        `MCO_ATRIBUTOS_FALTANTES_${fecha.toISOString().slice(0, 10)}.txt`;

      document.body.appendChild(enlace);
      enlace.click();
      enlace.remove();

      setTimeout(() => URL.revokeObjectURL(url), 30000);

      setEstadoExportacion(
        `Informe descargado: ${productosConFaltantes} productos con faltantes.` +
        (noVerificados
          ? ` ${noVerificados} no pudieron verificarse.`
          : '')
      );

    } catch (error) {
      console.error('[Informe MCO]', error);
      setEstadoExportacion(
        'Error al generar el informe: ' +
        (error?.message || String(error))
      );
    } finally {
      setExportandoFaltantes(false);
    }
  };

  const attributePreviewRequestKeyRef = useRef('');
  const isMountedRef = useRef(true);

  useEffect(() => {
    if (!isVisible) return;
    setManualCategoryId(batchCategoryId || '');
    setManualCategoryError('');
    setBulkChanges(prev => ({
      ...prev,
      category: batchCategoryId || '',
      categoryPath: batchCategoryPath || ''
    }));
    setCategoryPathSelections([]);
    setCategoryStatusMessage(batchCategoryId
      ? `Categoría hoja confirmada: ${batchCategoryId}.`
      : 'Este archivo no tiene una categoría detectada; requiere revisión.');
  }, [isVisible, sourceFileName, batchCategoryId, batchCategoryPath]);

  // Cargar categorías reales (nivel 1 + hijos) al montar
  const loadRootCategories = useCallback(async () => {
    if (!isMountedRef.current) return;
    setLoadingCategories(true);
    setCategoryLoadError(null);
    setLegacyCategoryOptions([]);
    let fetchedTopLevel = false;
    let loadedSuccessfully = false;
    try {
      const res = await mercadoLibreService.getCategories();
      if (isMountedRef.current && res?.success) {
        const formatted = (res.categories || []).map(cat => ({
          value: cat.id,
          label: `${cat.name || cat.id} (${cat.id})`,
          description: cat.name || cat.id
        }));
        if (formatted.length) {
          fetchedTopLevel = true;
          loadedSuccessfully = true;
          setCategoryLoadError(null);
          setRootCategoryOptions(formatted);
          setCategoryLevels([{ level: 1, parentId: null, options: formatted }]);
          setCategoryPathSelections([]);
          setCategoryStatusMessage('Selecciona una categoría y navega hasta una subcategoría hoja.');
        } else {
          setCategoryLoadError('MercadoLibre no devolvió categorías nivel raíz. Intentando fallback…');
        }
      } else if (isMountedRef.current) {
        setCategoryLoadError('No se pudo contactar a MercadoLibre para obtener categorías. Intentando fallback…');
      }

      const treeRes = await mercadoLibreService.getCategoryTree();
      if (isMountedRef.current && treeRes?.success) {
        const rootNodes = (treeRes.categories || []).filter(cat => {
          if (!cat?.id) return false;
          const path = cat?.path || '';
          return !path.includes(' > '); // nivel raíz
        });
        const leafNodes = (treeRes.categories || []).filter(cat => cat?.leaf === true && cat?.id);
        if (leafNodes.length) {
          const formattedLeaves = leafNodes.map(cat => ({
            value: cat.id,
            label: `${cat.path || cat.name || cat.id} (${cat.id})`,
            description: cat.name || cat.id
          }));
          setLegacyCategoryOptions(formattedLeaves);
        }

        if (rootNodes.length) {
          const formattedFallback = rootNodes.map(cat => ({
            value: cat.id,
            label: `${cat.name || cat.id} (${cat.id})`,
            description: cat.name || cat.id
          }));
          setCategoryLoadError(null);
          setRootCategoryOptions(formattedFallback);
          setCategoryLevels([{ level: 1, parentId: null, options: formattedFallback }]);
          setCategoryPathSelections([]);
          setCategoryStatusMessage('Selecciona una categoría y navega hasta una subcategoría hoja.');
          loadedSuccessfully = true;
          return;
        }

        if (!fetchedTopLevel && leafNodes.length) {
          setCategoryLevels([]);
          setCategoryPathSelections([]);
          setCategoryStatusMessage('Selecciona una categoría hoja del listado plano temporal.');
          setCategoryLoadError(null);
          loadedSuccessfully = true;
          return;
        }
      }
    } catch (e) {
      if (isMountedRef.current) {
        showError('Error cargando categorías');
        setCategoryLoadError('Ocurrió un error inesperado obteniendo categorías de MercadoLibre');
      }
    } finally {
      if (isMountedRef.current) setLoadingCategories(false);
    }

    if (isMountedRef.current && !loadedSuccessfully) {
      setCategoryLoadError('No se pudieron cargar las categorías reales de MercadoLibre');
    }
  }, [showError]);

  useEffect(() => {
    isMountedRef.current = true;
    loadRootCategories();
    return () => {
      isMountedRef.current = false;
    };
  }, [loadRootCategories]);

  const resetCategoryNavigator = () => {
    if (rootCategoryOptions.length === 0) {
      loadRootCategories();
      return;
    }
    setCategoryLevels([{ level: 1, parentId: null, options: rootCategoryOptions }]);
    setCategoryPathSelections([]);
    setCategoryStatusMessage('Selecciona una categoría y navega hasta una subcategoría hoja.');
    setBulkChanges(prev => ({ ...prev, category: '', categoryPath: '' }));
  };

  const handleCategoryLevelChange = async (levelIndex, selectedValue) => {
    const level = categoryLevels[levelIndex];
    if (!level) return;
    const selectedOption = level.options?.find(opt => opt.value === selectedValue);
    if (!selectedOption) return;

    const newPath = [...categoryPathSelections.slice(0, levelIndex), selectedOption];
    setCategoryPathSelections(newPath);
    const pathString = newPath.map(opt => opt.label).join(' > ');
    setBulkChanges(prev => ({
      ...prev,
      category: '',
      categoryPath: pathString
    }));
    setCategoryStatusMessage('Buscando subcategorías disponibles...');
    setLoadingChildrenLevel(true);

    const childRes = await mercadoLibreService.getCategoryChildren(selectedOption.value);
    setLoadingChildrenLevel(false);

    if (childRes?.success) {
      const children = childRes.children || [];
      if (children.length === 0) {
        setCategoryLevels(prev => prev.slice(0, levelIndex + 1));
        setBulkChanges(prev => ({
          ...prev,
          category: selectedOption.value,
          categoryPath: newPath.map(opt => opt.label).join(' > ')
        }));
        setCategoryStatusMessage('Categoría hoja lista para aplicar.');
        return;
      }

      const childOptions = children.map(child => ({
        value: child?.id,
        label: `${child?.name || child?.id} (${child?.id})`,
        description: child?.name || child?.id,
        leaf: Array.isArray(child?.children_categories) ? child.children_categories.length === 0 : false
      }));

      setCategoryLevels(prev => [
        ...prev.slice(0, levelIndex + 1),
        { level: levelIndex + 2, parentId: selectedOption.value, options: childOptions }
      ]);
      setCategoryStatusMessage('Esta categoría aún tiene subcategorías. Continúa navegando hasta una hoja.');
    } else {
      showError(childRes?.error || 'No se pudieron cargar las subcategorías');
      setCategoryStatusMessage('No se pudo recuperar el detalle. Intenta otra categoría.');
    }
  };

  const handleQuickLeafCategorySelect = (categoryId) => {
    if (!categoryId) {
      setBulkChanges(prev => ({ ...prev, category: '', categoryPath: '' }));
      setCategoryStatusMessage('Selecciona una categoría y navega hasta una subcategoría hoja.');
      return;
    }

    const option = legacyCategoryOptions.find(opt => opt?.value === categoryId);
    if (!option) {
      setBulkChanges(prev => ({ ...prev, category: '', categoryPath: '' }));
      return;
    }

    const labelWithoutId = (() => {
      const suffix = ` (${option.value})`;
      return option.label.endsWith(suffix)
        ? option.label.slice(0, option.label.length - suffix.length)
        : option.label;
    })();

    setBulkChanges(prev => ({
      ...prev,
      category: option.value,
      categoryPath: labelWithoutId
    }));
    setCategoryStatusMessage('Categoría hoja confirmada mediante búsqueda rápida.');
  };

  const extractCategoryId = (rawValue) => {
    if (typeof rawValue !== 'string') return '';
    const trimmed = rawValue.trim();
    if (!trimmed) return '';
    const uppercase = trimmed.toUpperCase();
    const match = uppercase.match(/[A-Z]{3}\d{3,}/);
    if (match) return match[0];
    return uppercase.replace(/[^A-Z0-9]/g, '');
  };

  const applyManualCategory = useCallback(async () => {
    if (manualCategoryLoading) return;
    const candidateId = extractCategoryId(manualCategoryId);
    if (!candidateId) {
      setManualCategoryError('Ingresa un ID de categoría válido, por ejemplo MCO123456.');
      return;
    }

    setManualCategoryError('');
    setManualCategoryLoading(true);

    try {
      const detail = await mercadoLibreService.getCategoryDetail(candidateId);
      if (!detail) {
        setManualCategoryError('MercadoLibre no devolvió datos para ese ID. Verifica que la categoría exista.');
        return;
      }

      const pathSegments = Array.isArray(detail?.path_from_root)
        ? detail.path_from_root.map((item) => item?.name).filter(Boolean)
        : [];
      const computedPath = pathSegments.length
        ? pathSegments.join(' > ')
        : (detail?.name || candidateId);
      const children = Array.isArray(detail?.children_categories)
        ? detail.children_categories
        : [];

      if (children.length > 0) {
        setManualCategoryError('La categoría ingresada todavía tiene subcategorías. Copia una categoría hoja específica.');
        return;
      }

      setBulkChanges(prev => ({
        ...prev,
        category: candidateId,
        categoryPath: computedPath
      }));
      setCategoryPathSelections([]);
      setCategoryStatusMessage(`Categoría hoja confirmada manualmente: ${computedPath} (${candidateId}).`);

      setLegacyCategoryOptions(prev => {
        if (prev.some(opt => opt?.value === candidateId)) {
          return prev;
        }
        const next = [
          ...prev,
          {
            value: candidateId,
            label: `${computedPath} (${candidateId})`,
            description: detail?.name || candidateId
          }
        ];
        next.sort((a, b) => (a?.label || '').localeCompare(b?.label || ''));
        return next;
      });
      setManualCategoryId(candidateId);
    } catch (error) {
      setManualCategoryError('Ocurrió un error consultando MercadoLibre. Intenta nuevamente.');
      console.error('applyManualCategory error:', error?.message || error);
    } finally {
      setManualCategoryLoading(false);
    }
  }, [manualCategoryId, manualCategoryLoading, setBulkChanges, setLegacyCategoryOptions]);

  useEffect(() => {
    const categoryId = String(bulkChanges?.category || '').trim();
    if (!isVisible || !categoryId || selectedProducts.length === 0) {
      setAttributePreviews([]);
      setAttributePreviewError('');
      setSelectedAttributePreviewKey('');
      setAttributePreviewMetrics(null);
      attributePreviewRequestKeyRef.current = '';
      return undefined;
    }

    const requestKey = `${categoryId}:${selectedProducts.map(product => String(product?.id ?? product?.asin ?? '')).join('|')}`;
    if (attributePreviewRequestKeyRef.current === requestKey) return undefined;
    attributePreviewRequestKeyRef.current = requestKey;

    let cancelled = false;
    setAttributePreviews([]);
    setSelectedAttributePreviewKey('');
    setAttributePreviewMetrics(null);
    setAttributePreviewLoading(true);
    setAttributePreviewError('');
    mercadoLibreService.previewPreparedAttributes(selectedProducts.map(product => ({
      ...product,
      category_id: categoryId
    }))).then((result) => {
      if (cancelled) return;
      if (!result?.success) {
        setAttributePreviews([]);
        setAttributePreviewMetrics(null);
        setAttributePreviewError(result?.error || 'No se pudieron preparar los atributos.');
        return;
      }
      const previews = (result.previews || []).map((preview, index) => ({
        ...preview,
        viewKey: `${preview?.id ?? preview?.asin ?? 'producto'}-${index}`
      }));
      setAttributePreviews(previews);
      setSelectedAttributePreviewKey(previews[0]?.viewKey || '');
      setAttributePreviewMetrics(result.requestMetrics || null);
    }).finally(() => {
      if (!cancelled) setAttributePreviewLoading(false);
    });

    return () => {
      cancelled = true;
    };
  }, [bulkChanges?.category, isVisible, selectedProducts]);

  const amazonTransportRows = [
    ['1', '3.90'],
    ['2', '4.00'],
    ['3', '5.95'],
    ['4', '6.95'],
    ['5–6', '8.82'],
    ['7–8', '10.70'],
    ['9–11', '12.57'],
    ['12–13', '14.44'],
    ['14–15', '16.31'],
    ['16–17', '18.18'],
    ['18–19', '20.05'],
    ['20–22', '21.92'],
    ['23', '23.79'],
    ['24', '24.00']
  ];

  const conditionOptions = [
    { value: 'new', label: 'Nuevo' },
    { value: 'used', label: 'Usado' },
    { value: 'not_specified', label: 'No especificado' }
  ];

  const priceAdjustmentTypes = [
    { value: 'percentage', label: 'Porcentaje (%)' },
    { value: 'fixed', label: 'Monto fijo ($)' }
  ];

  const tabs = [
    { id: 'category', label: 'Categoría', icon: 'Tag' },
    { id: 'pricing', label: 'Precios', icon: 'DollarSign' },
    // Descripción removida: ya se toma del archivo Excel/JSON
    { id: 'settings', label: 'Configuración', icon: 'Settings' },
    { id: 'store', label: 'Nombre de la tienda', icon: 'Store' }
  ];

  const handleBulkChange = (field, value) => {
    setBulkChanges(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleManufacturingTimeChange = (event) => {
    const value = event?.target?.value || '';
    const isValid = value === '' || /^(?:[1-9]|[1-5]\d|60)$/.test(value);
    setManufacturingTimeError(isValid ? '' : 'Ingresa un número entero entre 1 y 60.');
    handleBulkChange('manufacturingTimeDays', value);
  };

  const handleApplyChanges = async () => {
    if (applyingRef.current) return;
    if (
      bulkChanges?.manufacturingTimeDays &&
      !/^(?:[1-9]|[1-5]\d|60)$/.test(
        String(bulkChanges.manufacturingTimeDays).trim()
      )
    ) {
      setManufacturingTimeError(
        "Ingresa un número entero entre 1 y 60."
      );
      showError(
        "Corrige el tiempo de disponibilidad antes de aplicar los cambios."
      );
      return;
    }

    applyingRef.current = true;
    setApplyProgress({
      done: 0,
      total: selectedProducts?.length || 0
    });

    const value = (storeName || "").trim();

    try {
      if (typeof window !== "undefined") {
        const sellerId =
          window.localStorage.getItem("mercadolibre_user_id");

        if (sellerId) {
          window.localStorage.setItem(
            "ml_store_name:" + sellerId,
            value
          );
        }
      }
    } catch {}

    try {
      const pricingFinalPricesByAsin = {};
      const token =
        mercadoLibreService?.getAccessToken?.() || "";

      const queue = selectedProducts || [];
      const results = new Array(queue.length);
      let nextIndex = 0;
      let finished = 0;
      const pricingErrors = []; // MB_GUARDAR_ERRORES_PRECIO_V2

      const worker = async () => {
        while (nextIndex < queue.length) {
          const index = nextIndex++;
          const product = queue[index];

          try {
        const asin = String(
          product?.asin ||
          product?.ASIN ||
          product?.amazon_asin ||
          product?.originalAsin ||
          ""
        ).trim().toUpperCase();

        if (!asin) throw new Error('ASIN faltante');

        const pricingProduct = {
          ...product,

          category_id:
            bulkChanges?.category ||
            batchCategoryId ||
            product?.category_id ||
            product?.categoria ||
            product?.category,

          condition:
            bulkChanges?.condition ||
            product?.condition ||
            product?.condicion,

          free_shipping:
            typeof bulkChanges?.shipping?.free === "boolean"
              ? bulkChanges.shipping.free
              : product?.free_shipping,

          forceEnd999:
            bulkChanges?.priceAdjustment?.forceEnd999 === true
        };

        const response = await fetch(
          "/api/pricing/calculate",
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              ...(token
                ? { Authorization: "Bearer " + token }
                : {})
            },
            body: JSON.stringify({
              product: pricingProduct
            })
          }
        );

        const pricingResult =
          await response.json();

        if (!response.ok) {
          throw new Error(
            pricingResult?.message ||
            pricingResult?.error ||
            "No se pudo calcular el precio de " + asin
          );
        }

        const finalPrice =
          Number(pricingResult?.finalPriceCop);

        if (
          !Number.isFinite(finalPrice) ||
          finalPrice <= 0
        ) {
          throw new Error(
            "El motor no devolvió un precio válido para " + asin
          );
        }

        results[index] = [asin, Math.round(finalPrice)];

          } catch (error) {
            pricingErrors.push({
              productId: product?.id,
              asin: String(
                product?.asin ||
                product?.ASIN ||
                product?.amazon_asin ||
                product?.originalAsin ||
                ''
              ).trim().toUpperCase(),
              reason: error?.message || 'Error de cálculo de precio'
            });
          } finally {
            finished += 1;
            setApplyProgress({
              done: finished,
              total: queue.length
            });
          }
        }
      };

      await Promise.all(
        Array.from(
          { length: Math.min(3, queue.length) },
          () => worker()
        )
      );

      // Los fallidos se excluyen; los correctos continúan.

      for (const result of results) {
        if (result) {
          pricingFinalPricesByAsin[result[0]] = result[1];
        }
      }

      onBulkUpdate({
        ...bulkChanges,
        storeName: value,
        pricingFinalPricesByAsin,
        pricingErrors
      });

    } catch (error) {
      showError(
        error?.message ||
        "No se pudieron aplicar los precios calculados."
      );
    } finally {
      applyingRef.current = false;
      setApplyProgress(null);
    }
  };

  // Cargar nombre de tienda desde localStorage
  useEffect(() => {
    try {
      const saved = typeof window !== 'undefined' ? window.localStorage.getItem('mercadolibre_user_id') ? window.localStorage.getItem('ml_store_name:' + window.localStorage.getItem('mercadolibre_user_id')) : '' : '';
      if (saved) setStoreName(saved);
    } catch {}
  }, []);

  const handleStoreSave = () => {
    if (typeof window === 'undefined') return;
    const value = (storeName || '').trim();
    window.localStorage.getItem('mercadolibre_user_id') && window.localStorage.setItem('ml_store_name:' + window.localStorage.getItem('mercadolibre_user_id'), value);
    setStoreSavedAt(new Date().toISOString());
  };

  if (!isVisible || selectedCount === 0) {
    return null;
  }

  const selectedAttributePreview = attributePreviews.find(preview => preview.viewKey === selectedAttributePreviewKey)
    || attributePreviews[0];
  const effectivePreviewCategoryId = selectedAttributePreview?.category_id || bulkChanges?.category;
  const previewCategoryIsProvisional = selectedAttributePreview?.categoryResolution?.provisional === true;
  const attributeSummary = attributePreviews.reduce((summary, preview) => {
    const preparation = preview?.attributePreparation || {};
    summary.required += preparation.required?.length || 0;
    summary.present += preparation.present?.length || 0;
    summary.recovered += preparation.recovered?.length || 0;
    summary.notApplicable += preparation.notApplicable?.length || 0;
    summary.missing += preparation.missing?.length || 0;
    summary.failed += preview?.preparationError ? 1 : 0;
    return summary;
  }, { required: 0, present: 0, recovered: 0, notApplicable: 0, missing: 0, failed: 0 });
  const preparedAttributePreviewCount = attributePreviews.length - attributeSummary.failed;
  const largestPreviewRequestBytes = Math.max(
    0,
    ...(attributePreviewMetrics?.sent || []).map(request => request.bytes || 0)
  );
  const realAttributeValues = selectedAttributePreview?.attributePreparation?.present
    ?.filter(attribute => attribute?.source === 'attributes') || [];
  const modelPreparation = selectedAttributePreview?.attributePreparation?.model;
  const modelStatusLabel = {
    original: 'MODEL original conservado',
    recovered: 'MODEL recuperado',
    lost_during_processing: 'MODEL perdido durante el procesamiento',
    missing: 'MODEL sin dato verificable'
  }[modelPreparation?.status] || 'MODEL sin preparar';

  return (
    <div className="rounded-xl border border-border bg-surface shadow-[0_12px_30px_rgba(15,23,42,0.18)] overflow-hidden">
      {/* Header */}
      <div className="border-b border-border bg-muted/30 px-4 py-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <div className="flex h-9 w-9 items-center justify-center rounded-full bg-primary/10 text-primary">
              <Icon name="Edit" size={16} />
            </div>
            <div>
              <h3 className="font-semibold text-text-primary">Edición Masiva</h3>
              <p className="text-sm text-text-secondary">
                {selectedCount} productos seleccionados
              </p>
            </div>
          </div>
          <Button
            variant="ghost"
            size="sm"
            iconName="X"
            onClick={onClearSelection}
          />
        </div>
      </div>
      {/* Tabs */}
      <div className="border-b border-border bg-surface/80">
        <div className="flex overflow-x-auto">
          {tabs?.map((tab) => (
            <button
              key={tab?.id}
              onClick={() => setActiveTab(tab?.id)}
              className={`flex items-center space-x-2 border-b-2 px-4 py-3 text-sm font-medium whitespace-nowrap transition-colors ${
                activeTab === tab?.id
                  ? 'border-primary bg-primary/5 text-primary'
                  : 'border-transparent text-text-secondary hover:bg-muted/50 hover:text-text-primary'
              }`}
            >
              <Icon name={tab?.icon} size={16} />
              <span>{tab?.label}</span>
            </button>
          ))}
        </div>
      </div>
      {/* Content */}
      <div className="p-4">
        {activeTab === 'category' && (
          <div className="space-y-4">
            {categoryLoadError && (
              <div className="rounded-md border border-amber-200 bg-amber-50 p-3 text-sm text-amber-700 dark:border-amber-800/60 dark:bg-amber-950/30 dark:text-amber-300">
                {categoryLoadError}
              </div>
            )}

            {categoryLevels.length === 0 && legacyCategoryOptions.length === 0 ? (
              <div className="text-sm text-text-secondary bg-muted/40 p-3 rounded-md">
                No se pudieron cargar las categorías en este momento. Intenta recargar la página.
              </div>
            ) : categoryLevels.length > 0 ? (
              categoryLevels.map((level, idx) => (
                <Select
                  key={level?.parentId ?? `root-${idx}`}
                  label={idx === 0 ? 'Categoría principal' : `Subcategoría nivel ${idx + 1}`}
                  description={idx === 0
                    ? 'Selecciona la familia principal del producto.'
                    : 'Refina tu selección hasta llegar a una hoja.'}
                  options={level?.options || []}
                  value={categoryPathSelections[idx]?.value || ''}
                  onChange={(value) => handleCategoryLevelChange(idx, value)}
                  searchable
                  loading={idx === 0 ? loadingCategories : loadingChildrenLevel}
                  placeholder="Seleccionar opción..."
                />
              ))
            ) : (
              <Select
                label="Listado plano de categorías"
                description="Selecciona directamente una categoría hoja (modo temporal)."
                options={legacyCategoryOptions}
                value={bulkChanges?.category}
                onChange={handleQuickLeafCategorySelect}
                searchable
                clearable
                loading={loadingCategories}
                placeholder="Buscar categoría hoja..."
              />
            )}

            {categoryLevels.length > 0 && legacyCategoryOptions.length > 0 && (
              <Select
                label="Búsqueda rápida en todas las categorías"
                description="Escribe para encontrar la categoría hoja exacta (Televisores, Refrigeradores, etc.)."
                options={legacyCategoryOptions}
                value={bulkChanges?.category}
                onChange={handleQuickLeafCategorySelect}
                searchable
                clearable
                placeholder="Buscar categoría hoja..."
              />
            )}

            <div className="space-y-2">
              <Input
                label="Ingresar manualmente el ID de categoría"
                description="Pega la clave completa, por ejemplo Calefactores > Eléctricos (MCO567890)."
                value={manualCategoryId}
                onChange={(event) => {
                  setManualCategoryId(event?.target?.value || '');
                  if (manualCategoryError) setManualCategoryError('');
                }}
                onKeyDown={(event) => {
                  if (event.key === 'Enter') {
                    event.preventDefault();
                    applyManualCategory();
                  }
                }}
                error={manualCategoryError}
                placeholder="Ej. MCO123456"
              />
              <div className="flex items-center gap-2">
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={applyManualCategory}
                  disabled={manualCategoryLoading}
                >
                  Validar categoría manual
                </Button>
                {manualCategoryLoading && (
                  <span className="text-xs text-text-secondary">Verificando con MercadoLibre…</span>
                )}
              </div>
            </div>

            {(categoryPathSelections.length > 0 || bulkChanges?.categoryPath) && (
              <div className="text-sm text-text-secondary">
                Ruta seleccionada: {categoryPathSelections.length > 0
                  ? categoryPathSelections.map(opt => opt?.label).join(' > ')
                  : bulkChanges?.categoryPath}
              </div>
            )}

            {bulkChanges?.category ? (
              <div className="text-sm text-success dark:text-emerald-300">
                Categoría hoja confirmada: {bulkChanges.category}
              </div>
            ) : (
              <div className="text-sm text-amber-600 dark:text-amber-300">
                {categoryStatusMessage}
              </div>
            )}

            {attributePreviewLoading && (
              <div className="text-xs text-text-secondary">Preparando atributos para la categoría validada…</div>
            )}

            {attributePreviewError && (
              <div className="text-xs text-amber-700 dark:text-amber-300">{attributePreviewError}</div>
            )}

            {selectedAttributePreview && (
              <div className="rounded-2xl border border-border bg-white p-4 text-sm shadow-sm dark:bg-slate-900 sm:p-5">
                <div className="flex flex-wrap items-center justify-between gap-3 border-b border-border pb-4">
                  <div className="text-base font-bold tracking-tight text-text-primary sm:text-lg">Atributos para {effectivePreviewCategoryId}</div>
                  <div className="rounded-full border border-blue-200 bg-blue-50 px-3 py-1 text-xs font-semibold text-blue-700 dark:border-blue-900 dark:bg-blue-950/40 dark:text-blue-300">
                    {preparedAttributePreviewCount} preparado{preparedAttributePreviewCount !== 1 ? 's' : ''}
                    {attributeSummary.failed > 0 && ` · ${attributeSummary.failed} con error`}
                  </div>
                </div>

                {attributePreviewMetrics?.sent?.length > 0 && (
                  <div className="mt-3 rounded-lg bg-slate-50 px-3 py-2 text-xs text-text-secondary dark:bg-slate-800/60">
                    {attributePreviewMetrics.sent.length} solicitud{attributePreviewMetrics.sent.length !== 1 ? 'es' : ''} · mayor cuerpo {largestPreviewRequestBytes.toLocaleString('es-CO')} bytes
                    {attributePreviewMetrics.subdivisions > 0 && ` · ${attributePreviewMetrics.subdivisions} subdivisión${attributePreviewMetrics.subdivisions !== 1 ? 'es' : ''} por 413`}
                  </div>
                )}

                {selectedAttributePreview.preparationError && (
                  <div className="mt-3 rounded-md border border-amber-200 bg-amber-50 p-3 text-xs text-amber-700 dark:border-amber-800/60 dark:bg-amber-950/30 dark:text-amber-300">
                    <div className="font-medium">Este producto no fue preparado</div>
                    <div>{selectedAttributePreview.preparationError.message}</div>
                    {selectedAttributePreview.preparationError.requestBytes > 0 && (
                      <div className="mt-1">Cuerpo medido: {selectedAttributePreview.preparationError.requestBytes.toLocaleString('es-CO')} bytes.</div>
                    )}
                  </div>
                )}

                {!selectedAttributePreview.preparationError && selectedAttributePreview.attributePreparation?.metadataSource === 'catalogo_local' && (
                  <div className="mt-3 rounded-lg border border-amber-200 bg-amber-50 p-3 text-xs text-amber-700 dark:border-amber-900 dark:bg-amber-950/30 dark:text-amber-300">
                    Requisitos obtenidos del catálogo local; MercadoLibre no respondió con metadatos vigentes.
                  </div>
                )}

                {!selectedAttributePreview.preparationError && (previewCategoryIsProvisional ? (
                  <div className="mt-3 rounded-lg border border-amber-200 bg-amber-50 p-3 text-xs text-amber-700 dark:border-amber-900 dark:bg-amber-950/30 dark:text-amber-300">
                    Categoría provisional: los atributos pueden cambiar cuando MercadoLibre confirme la categoría final.
                  </div>
                ) : effectivePreviewCategoryId !== bulkChanges.category ? (
                  <div className="mt-3 rounded-lg bg-slate-50 p-3 text-xs text-text-secondary dark:bg-slate-800/60">
                    Categoría ajustada durante la preparación: {effectivePreviewCategoryId}.
                  </div>
                ) : null)}

                {attributePreviews.length > 1 && (
                  <div className="mt-4 rounded-xl border border-border bg-slate-50/60 p-3 dark:bg-slate-800/30">
                    <Select
                      label="Consultar producto"
                      options={attributePreviews.map((preview) => ({
                        value: preview.viewKey,
                        label: preview.asin || `Producto ${preview.id || ''}`
                      }))}
                      value={selectedAttributePreview.viewKey}
                      onChange={setSelectedAttributePreviewKey}
                      searchable
                    />
                  </div>
                )}

                {!selectedAttributePreview.preparationError && (
                  <div className="mt-4 grid grid-cols-2 gap-2 sm:grid-cols-3 xl:grid-cols-5">
                    <div className="rounded-xl border border-blue-100 bg-blue-50 p-3 dark:border-blue-900/60 dark:bg-blue-950/30"><div className="text-2xl font-bold text-blue-700 dark:text-blue-300">{attributeSummary.required}</div><div className="mt-1 text-xs font-medium text-blue-800 dark:text-blue-200">Obligatorios</div></div>
                    <div className="rounded-xl border border-emerald-100 bg-emerald-50 p-3 dark:border-emerald-900/60 dark:bg-emerald-950/30"><div className="text-2xl font-bold text-emerald-700 dark:text-emerald-300">{attributeSummary.present}</div><div className="mt-1 text-xs font-medium text-emerald-800 dark:text-emerald-200">Con valor</div></div>
                    <div className="rounded-xl border border-violet-100 bg-violet-50 p-3 dark:border-violet-900/60 dark:bg-violet-950/30"><div className="text-2xl font-bold text-violet-700 dark:text-violet-300">{attributeSummary.recovered}</div><div className="mt-1 text-xs font-medium text-violet-800 dark:text-violet-200">Recuperados</div></div>
                    <div className="rounded-xl border border-slate-100 bg-slate-50 p-3 dark:border-slate-900/60 dark:bg-slate-950/30"><div className="text-2xl font-bold text-slate-700 dark:text-slate-300">{attributeSummary.notApplicable}</div><div className="mt-1 text-xs font-medium text-slate-800 dark:text-slate-200">N/A justificados</div></div>
                    <div className="rounded-xl border border-amber-100 bg-amber-50 p-3 dark:border-amber-900/60 dark:bg-amber-950/30"><div className="text-2xl font-bold text-amber-700 dark:text-amber-300">{attributeSummary.missing}</div><div className="mt-1 text-xs font-medium text-amber-800 dark:text-amber-200">Sin encontrar</div></div>
                  </div>
                )}

                {!selectedAttributePreview.preparationError && <div className="mt-4 grid grid-cols-1 gap-3 text-xs text-text-secondary lg:grid-cols-[minmax(0,1.7fr)_minmax(0,1fr)]">
                  <div className="space-y-3">
                  <div className="rounded-xl border border-rose-100 bg-rose-50/60 p-4 dark:border-rose-900/50 dark:bg-rose-950/20">
                    <div className="mb-2 text-sm font-semibold text-rose-800 dark:text-rose-300">Obligatorios</div>
                    <div>{selectedAttributePreview.attributePreparation?.required?.map(attribute => attribute.name || attribute.id).join(' · ') || 'Ninguno'}</div>
                  </div>
                  <div className="rounded-xl border border-border bg-slate-50/60 p-4 dark:bg-slate-800/40">
                    <div className="mb-2 text-sm font-semibold text-text-primary">Opcionales</div>
                    <div>{selectedAttributePreview.attributePreparation?.optional?.slice(0, 24).map(attribute => attribute.name || attribute.id).join(' · ') || 'Ninguno'}</div>
                  </div>
                  </div>
                  <div className="space-y-3">
                  <div className="rounded-xl border border-border bg-white p-3 dark:bg-slate-900">
                    <div className="mb-1 text-sm font-semibold text-text-primary">Con valor real</div>
                    <div>{realAttributeValues.map(attribute => `${attribute.id}: ${attribute.value_name || attribute.value_id}`).join(' · ') || 'Ninguno'}</div>
                  </div>
                  <div className={`rounded-xl border border-border bg-white p-3 dark:bg-slate-900 ${modelPreparation?.status === 'lost_during_processing' ? 'text-amber-700 dark:text-amber-300' : ''}`}>
                    <div className="mb-1 text-sm font-semibold text-text-primary">{modelStatusLabel}</div>
                    <div>{modelPreparation?.value || modelPreparation?.reason || 'Sin dato' }{modelPreparation?.source ? ` · Fuente: ${modelPreparation.source}` : ''}</div>
                  </div>
                  <div className="rounded-xl border p-3 border-border bg-white dark:bg-slate-900">
                    <div className="mb-1 text-sm font-semibold text-text-primary">Recuperados desde el producto</div>
                    <div>{selectedAttributePreview.attributePreparation?.recovered?.map(attribute => `${attribute.id}: ${attribute.value_name || attribute.value_id}`).join(' · ') || 'Ninguno'}</div>
                  </div>
                  <div className="rounded-xl border p-3 border-border bg-white dark:bg-slate-900">
                    <div className="mb-1 text-sm font-semibold text-text-primary">N/A justificado</div>
                    <div>{selectedAttributePreview.attributePreparation?.notApplicable?.map(attribute => `${attribute.id}: -1`).join(' · ') || 'Ninguno'}</div>
                  </div>
                  <div className="rounded-xl border p-3 border-amber-100 bg-amber-50/60 dark:border-amber-900/50 dark:bg-amber-950/20">
                    <div className="mb-1 text-sm font-semibold text-text-primary">Pendientes</div>
                    <div>{selectedAttributePreview.attributePreparation?.missing?.map(attribute => attribute.id).join(' · ') || 'Ninguno'}</div>
                  </div>
                  {selectedAttributePreview.attributePreparation?.classificationWarnings?.length > 0 && (
                    <div className="rounded-xl border border-amber-200 bg-amber-50 p-3 text-amber-700 dark:border-amber-900 dark:bg-amber-950/30 dark:text-amber-300">
                      <div className="mb-1 text-sm font-semibold">Revisar clasificación</div>
                      <div>La categoría exige {selectedAttributePreview.attributePreparation.classificationWarnings.map(attribute => attribute.id).join(' · ')}, pero el origen indica que no aplica y MercadoLibre no permite N/A.</div>
                    </div>
                  )}
                  <div className="rounded-xl border p-3 border-border bg-white dark:bg-slate-900">
                    <div className="mb-1 text-sm font-semibold text-text-primary">Conflictos de valores</div>
                    <div>{selectedAttributePreview.attributePreparation?.unresolved?.map(attribute => attribute.id).join(' · ') || 'Ninguno'}</div>
                  </div>
                  </div>
                </div>}

                {/* MCO: exportación de faltantes, solo lectura */}
                <div className="mt-4 flex flex-wrap items-center justify-end gap-3 border-t border-border pt-3">


                  <Button
                    variant="outline"
                    size="sm"
                    iconName="Download"
                    iconPosition="left"
                    disabled={exportandoFaltantes || !attributeExportBatches.length}
                    onClick={descargarFaltantesMCO}
                  >
                    {exportandoFaltantes
                      ? (estadoExportacion || 'Revisando archivos...')
                      : 'Descargar atributos faltantes (.txt)'}
                  </Button>
                </div>
              </div>
            )}

            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={resetCategoryNavigator}
                disabled={loadingCategories}
              >
                {rootCategoryOptions.length === 0 ? 'Reintentar carga' : 'Reiniciar selección'}
              </Button>
              {loadingChildrenLevel && (
                <span className="text-xs text-text-secondary">Cargando subcategorías...</span>
              )}
            </div>
          </div>
        )}

        {activeTab === 'pricing' && (
          <div className="space-y-4">

            {/* TRM: SE CONSERVA COMO ESTABA */}
            <div
              className="flex flex-wrap items-center gap-x-2 gap-y-1 border-b border-border pb-3 text-sm"
              aria-live="polite"
            >
              <Icon
                name="BadgeDollarSign"
                size={18}
                className="text-success"
              />

              {usdToCopRateStatus === 'loading' && (
                <span className="text-text-secondary">
                  USD → COP: Consultando tasa...
                </span>
              )}

              {formattedUsdToCopRate && (
                <>
                  <span className="font-medium text-text-primary">
                    USD → COP: {formattedUsdToCopRate}
                  </span>

                  {formattedUsdToCopFetchedAt && (
                    <span className="text-text-secondary">
                      · Actualizada: {formattedUsdToCopFetchedAt}
                    </span>
                  )}
                </>
              )}

              {usdToCopRateStatus === 'unavailable' && (
                <span className="text-text-secondary">
                  USD → COP: Tasa no disponible
                </span>
              )}
            </div>

            {/* TABLA DE TRANSPORTE AMAZON */}
            <div className="border border-border rounded-lg overflow-hidden">
              <div className="px-4 py-3 border-b border-border">
                <div className="font-semibold text-text-primary">
                  Tabla de transporte Amazon por peso facturable
                </div>
              </div>

              <div className="overflow-x-auto">
                <table className="w-full min-w-[900px] text-sm">
                  <tbody>
                    <tr className="border-b border-border">
                      <th className="text-left px-4 py-2 bg-muted/40 whitespace-nowrap">
                        Peso (lb)
                      </th>

                      {amazonTransportRows.map(([weight]) => (
                        <td
                          key={`weight-${weight}`}
                          className="text-center px-3 py-2 whitespace-nowrap"
                        >
                          {weight}
                        </td>
                      ))}
                    </tr>

                    <tr>
                      <th className="text-left px-4 py-2 bg-muted/40 whitespace-nowrap">
                        Transporte (USD)
                      </th>

                      {amazonTransportRows.map(([weight, cost]) => (
                        <td
                          key={`cost-${weight}`}
                          className="text-center px-3 py-2 whitespace-nowrap"
                        >
                          {cost}
                        </td>
                      ))}
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>

            <PricingPreview
              selectedProducts={selectedProducts}
            />

            {/* TERMINACIÓN EN 999: VUELVE ABAJO COMO ESTABA */}
            <div className="space-y-3">
              <Checkbox
                label="Aplicar"
                description="Automáticamente ajusta todos los precios para que terminen en 999"
                checked={
                  bulkChanges?.priceAdjustment?.forceEnd999 || false
                }
                onChange={(e) =>
                  handleBulkChange('priceAdjustment', {
                    ...bulkChanges?.priceAdjustment,
                    forceEnd999: e?.target?.checked
                  })
                }
              />
            </div>

          </div>
        )}

        {/* Pestaña de descripción removida: la descripción proviene del archivo */}

        {activeTab === 'settings' && (
          <div className="space-y-4">
            <Select
              label="Condición del Producto"
              options={conditionOptions}
              value={bulkChanges?.condition}
              onChange={(value) => handleBulkChange('condition', value)}
              placeholder="Seleccionar condición..."
            />
            
            <div className="space-y-3">
              <label className="block text-sm font-medium text-text-primary">
                Configuración de Envío
              </label>
              <Checkbox
                label="Envío gratis"
                description="Ofrecer envío gratuito en todos los productos"
                checked={bulkChanges?.shipping?.free}
                onChange={(e) => handleBulkChange('shipping', {
                  ...bulkChanges?.shipping,
                  free: e?.target?.checked
                })}
              />
            </div>

            <div className="space-y-3">
              <h4 className="text-sm font-medium text-text-primary">Disponibilidad del producto</h4>
              <Input
                label="Tiempo de disponibilidad"
                type="number"
                min="1"
                max="60"
                step="1"
                value={bulkChanges?.manufacturingTimeDays || ''}
                onChange={handleManufacturingTimeChange}
                description="Indica en cuántos días estará disponible el producto para despacho."
                error={manufacturingTimeError}
                placeholder="Ej. 15"
              />
            </div>
          </div>
        )}

        {activeTab === 'store' && (
          <div className="space-y-4">
            <div className="p-3 bg-primary/5 rounded-lg border border-primary/20">
              <div className="flex items-start space-x-2">
                <Icon name="Store" size={16} className="text-primary mt-0.5" />
                <div className="text-sm">
                  <p className="font-medium text-text-primary mb-1">Nombre de la tienda</p>
                  <p className="text-text-secondary">Este nombre se insertará automáticamente en la plantilla de descripción al publicar.</p>
                </div>
              </div>
            </div>

            <Input
              label="Nombre para mostrar"
              description="Ej: KENAY_STORE"
              value={storeName}
              onChange={(e) => setStoreName(e?.target?.value || '')}
              placeholder="Tu nombre de tienda"
            />

            <div className="flex items-center gap-2">
              <Button variant="default" size="sm" iconName="Save" iconPosition="left" onClick={handleStoreSave}>
                Guardar nombre
              </Button>
              {storeSavedAt && (
                <span className="text-xs text-text-secondary">Guardado: {new Date(storeSavedAt).toLocaleString()}</span>
              )}
            </div>

            <div className="mt-2 p-3 rounded-md border border-border bg-muted">
              <pre className="whitespace-pre-wrap text-xs">
{`BIENVENIDOS SOMOS ${(storeName || '').trim() ? storeName : 'TU_TIENDA'} !\nLIDERES EN MERCADOLIBRE EN PRODUCTOS IMPORTADOS CON 100% DE CALIFICACIONES POSITIVAS!\n\n---------------------------------------------------------------------------------------------------------------\nTIEMPOS DE ENTREGA DE 6 A 10 DIAS HABILES / ENVIO GRATIS\n---------------------------------------------------------------------------------------------------------------\n\nDESCRIPCION DEL PRODUCTO OFERTADO :\n...\n\n---------------------------------------------------------------------------------------------------------------\n\nRECUERDA SOMOS ${(storeName || '').trim() ? storeName : 'TU_TIENDA'}\nESTAMOS ENTRE LOS VENDEDORES MAS CONFIABLES DE MERCADOLIBRE !\n\nSin garantía`}
              </pre>
            </div>
          </div>
        )}

        {/* Actions */}
        <div className="flex flex-col sm:flex-row space-y-2 sm:space-y-0 sm:space-x-3 pt-4 border-t border-border mt-6">
          <Button
            variant="primary"
            iconName="Check"
            iconPosition="left"
            onClick={handleApplyChanges}
            disabled={Boolean(applyProgress)}
            fullWidth
          >
            {applyProgress
              ? `Aplicando cambios: ${applyProgress.done} de ${applyProgress.total}`
              : `Aplicar Cambios (${selectedCount} productos)`}
          </Button>
          <Button
            variant="outline"
            iconName="RotateCcw"
            onClick={() => {
              setManufacturingTimeError('');
              setBulkChanges({
                category: '',
                priceAdjustment: { type: 'percentage', value: 0 },
                condition: '',
                shipping: { free: false, mode: 'me2' },
                manufacturingTimeDays: ''
              });
            }}
          >
            Restablecer
          </Button>
        </div>
      </div>
    </div>
  );
};

export default BulkEditPanel;